import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { IWasteManagement } from '../../pages/product-details/components/product-tab-view/tabs/pcf-tab/components/pcf-wizard/PcfWizard.type';
import AmplifyApiService from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import {
  CREATION_METHOD,
  FootPrintStatus,
  ICalculationDetails,
  ICalculationDetailsTableRow,
  ICalculationResponse,
  IFootPrintDetails,
  IFootPrintTableRow,
  IProcessDetails,
  PartialFullPcf,
  PCFEntity,
} from '../../shared/interfaces/IFootprint';

const amplifyApiService = new AmplifyApiService();

const BASE_PATH = 'footprints';

interface IFootprintPdfDownloadUrl {
  downloadUrl: string;
}

export type CreatePcfProps = {
  product_id: number;
  date_created?: string;
  product_code_cpc?: string;
  declared_unit?: string;
  unitary_product_amount?: number;
  product_mass_per_declared_unit?: number | null;
  creation_method: CREATION_METHOD;
  calculation?: Omit<ICalculationDetails, 'use_of_energy'> & {
    use_of_energy_id?: number;
  };
};

export type CratePcfResponse = {
  id: string;
  footprintId: string;
};

export type UpdateFootprintDetailsProps = {
  pcfId?: number;
  status?: FootPrintStatus;
  date_created?: string;
  partial_full_pcf?: PartialFullPcf;
  product_code_cpc?: string;
  pcf?: Partial<PCFEntity>;
  creation_method?: CREATION_METHOD;
  calculation?: Omit<ICalculationDetails, 'use_of_energy'> & {
    use_of_energy_id?: number | null;
    processes?: IProcessDetails[];
  };
  pcf_legal_statement?: string;
};

export type CopyFootprintDetailsProps = {
  pcfId: number;
};

export type CopyFootPrintResponse = {
  footprintId: string;
};

export type CreateCalculationProps = Omit<ICalculationDetails, 'use_of_energy' | 'processes'> & {
  use_of_energy_id?: number;
  footprintId: number;
  processes?: (Omit<IProcessDetails, 'waste_management'> & {
    waste_management?: Omit<IWasteManagement, 'sprue_waste_treatment' | 'scrap_waste_treatment'> & {
      sprue_waste_treatment_id?: number | null;
      scrap_waste_treatment_id?: number | null;
    };
  })[];
};

export type UpdateCalculationProps = Omit<ICalculationDetails, 'use_of_energy' | 'processes'> & {
  name?: string;
  pcf_legal_statement?: string;
  calculationId: number;
  use_of_energy_id?: number;
  footprintId: number;
  processes?: (Omit<IProcessDetails, 'waste_management'> & {
    waste_management?: Omit<IWasteManagement, 'sprue_waste_treatment' | 'scrap_waste_treatment'> & {
      sprue_waste_treatment_id?: number | null;
      scrap_waste_treatment_id?: number | null;
    };
  })[];
};

export type PublishCalculationProps = {
  footprintId: number;
  calculationId: number;
};

export type CalculationResponse = { id: number };

type DiscardPcfProps = {
  footprintId: number;
};

/**
 * This function retrieves footprints by product ID using a specified API configuration and query
 * options.
 * @param {string} productId - The `productId` parameter is a string that represents the unique
 * identifier of a product for which you want to retrieve footprints.
 * @param options - The `options` parameter in the `useGetFootprintsByProductId` function is used to
 * provide additional configuration options for the query. These options can include settings such as
 * caching, polling intervals, error handling, and more. By default, an empty object is provided as the
 * default value for the `
 * @returns The `useGetFootprintsByProductId` custom hook is being returned. This hook is used to fetch
 * footprints data by product ID using a specified API configuration and query function.
 */
export const useGetFootprintsByProductId = (
  productId: string,
  options: UseQueryOptions<IFootPrintTableRow[], Error> = {},
): UseQueryResult<IFootPrintTableRow[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams: {
        productId,
      },
    },
  };

  const query = useQuery<IFootPrintTableRow[], Error>({
    queryKey: [BASE_PATH, 'pId', productId],
    queryFn: async (): Promise<IFootPrintTableRow[]> => {
      const response = await amplifyApiService.get<IFootPrintTableRow[]>(amplifyApiConfig);

      return response || [];
    },
    ...options,
  });

  return query;
};

/**
 * The function `useGetFootprintById` retrieves a product footprint by its ID using an API call and
 * returns the query result.
 * @param {number} footprintId - The `footprintId` parameter is a number that represents the unique
 * identifier of a footprint.
 * @param options - The `options` parameter in the `useGetFootprintById` function is used to provide
 * additional configuration options for the query. These options can include settings such as caching
 * behavior, polling intervals, error handling, and more. By default, the `options` parameter is an
 * empty object `{}` but
 * @returns The `useGetFootprintById` custom hook is being returned, which takes a `footprintId` string
 * and optional `options` object as parameters and returns a `UseQueryResult<IFootPrintDetails[]>` object. This
 * hook fetches data for a specific footprint by its ID using the `amplifyApiService` and returns the
 * response as an array of `IFootPrintDetails` objects.
 */
export const useGetFootprintById = (
  footprintId: number,
  options: UseQueryOptions<IFootPrintDetails, Error> = {},
): UseQueryResult<IFootPrintDetails> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${footprintId}`,
    options: {},
  };

  const query = useQuery<IFootPrintDetails, Error>({
    queryKey: [BASE_PATH, 'id', footprintId],
    queryFn: async (): Promise<IFootPrintDetails> => {
      const response = await amplifyApiService.get<IFootPrintDetails>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

/**
 * Custom hook to create a Product Carbon Footprint (PCF) using a mutation.
 *
 * @param options - Optional configuration for the mutation, extending the default mutation options.
 * @returns A mutation result object that includes methods and state for the mutation.
 *
 * This hook utilizes the AmplifyApiService to send a POST request to the 'footprints' endpoint
 * of the 'PacificApi'. The request body is constructed from the provided CreatePcfProps.
 */
export const useCreatePcf = (
  options: UseMutationOptions<CratePcfResponse, Error, CreatePcfProps> = {},
): UseMutationResult<CratePcfResponse, Error, CreatePcfProps> => {
  const mutation = useMutation<CratePcfResponse, Error, CreatePcfProps>({
    mutationFn: async (body: CreatePcfProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: BASE_PATH,
        options: { body },
      };

      return await amplifyApiService.post<CreatePcfProps, CratePcfResponse>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

/**
 * Custom hook to update footprint details using a mutation.
 *
 * This hook utilizes the `useMutation` hook from `@tanstack/react-query` to perform
 * an asynchronous update operation on footprint details. It sends a PUT request
 * to the 'PacificApi' endpoint with the specified `pcfId` and request body.
 *
 * @param options - Optional mutation options to configure the mutation behavior.
 * @returns A mutation result object containing the mutation state and functions.
 */
export const useUpdateFootprintDetails = (
  options: UseMutationOptions<boolean, Error, UpdateFootprintDetailsProps> = {},
): UseMutationResult<boolean, Error, UpdateFootprintDetailsProps> => {
  const mutation = useMutation<boolean, Error, UpdateFootprintDetailsProps>({
    mutationFn: async ({ pcfId, ...body }: UpdateFootprintDetailsProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${pcfId}`,
        options: { body },
      };

      return amplifyApiService.put<Partial<UpdateFootprintDetailsProps>, boolean>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useCopyFootprintDetails = (
  options: UseMutationOptions<CopyFootPrintResponse, Error, CopyFootprintDetailsProps> = {},
): UseMutationResult<CopyFootPrintResponse, Error, CopyFootprintDetailsProps> => {
  const mutation = useMutation<CopyFootPrintResponse, Error, CopyFootprintDetailsProps>({
    mutationFn: async ({ pcfId }: CopyFootprintDetailsProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${pcfId}/copy`,
      };

      return amplifyApiService.post<undefined, CopyFootPrintResponse>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useDiscardFootprint = (
  options: UseMutationOptions<void, Error, DiscardPcfProps> = {},
): UseMutationResult<void, Error, DiscardPcfProps> => {
  const mutation = useMutation<void, Error, DiscardPcfProps>({
    mutationFn: async ({ footprintId }: DiscardPcfProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${footprintId}`,
        options: {},
      };

      return await amplifyApiService.delete<undefined, void>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useCreateCalculation = (
  options: UseMutationOptions<CalculationResponse, Error, CreateCalculationProps> = {},
): UseMutationResult<CalculationResponse, Error, CreateCalculationProps> => {
  const mutation = useMutation<CalculationResponse, Error, CreateCalculationProps>({
    mutationFn: async ({ footprintId, ...body }: CreateCalculationProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${footprintId}/calculations`,
        options: { body },
      };

      return await amplifyApiService.post<CreateCalculationProps, CalculationResponse>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

export const useUpdateCalculation = (
  options: UseMutationOptions<CalculationResponse, Error, UpdateCalculationProps> = {},
): UseMutationResult<CalculationResponse, Error, UpdateCalculationProps> => {
  const mutation = useMutation<CalculationResponse, Error, UpdateCalculationProps>({
    mutationFn: async ({ calculationId, footprintId, ...body }: UpdateCalculationProps) => {
      const path =
        'name' in body || 'pcf_legal_statement' in body
          ? `${BASE_PATH}/${footprintId}/calculations/${calculationId}/update-complete`
          : `${BASE_PATH}/${footprintId}/calculations/${calculationId}`;

      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: path,
        options: { body },
      };

      return amplifyApiService.put<Partial<UpdateCalculationProps>, CalculationResponse>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

/**
 * The function `useGetCalculationsByFootPrintId` retrieves the calculations of footprint by its ID using an API call and
 * returns the query result.
 * @param {number} footprintId - The `footprintId` parameter is a number that represents the unique
 * identifier of a footprint.
 * @param options - The `options` parameter in the `useGetCalculationsByFootPrintId` function is used to provide
 * additional configuration options for the query. These options can include settings such as caching
 * behavior, polling intervals, error handling, and more. By default, the `options` parameter is an
 * empty object `{}` but
 * @returns The `useGetCalculationsByFootPrintId` custom hook is being returned, which takes a `footprintId` string
 * and optional `options` object as parameters and returns a `UseQueryResult<ICalculationDetailsTableRow[]>` object. This
 * hook fetches data for a specific calculations of footprint by its ID using the `amplifyApiService` and returns the
 * response as an array of `ICalculationDetailsTableRow` object.
 */
export const useGetCalculationsByFootPrintId = (
  footprintId: number,
  options: UseQueryOptions<ICalculationDetailsTableRow[], Error> = {},
): UseQueryResult<ICalculationDetailsTableRow[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${footprintId}/calculations`,
    options: {},
  };

  const query = useQuery<ICalculationDetailsTableRow[], Error>({
    queryKey: [BASE_PATH, 'calculations', footprintId],
    queryFn: async (): Promise<ICalculationDetailsTableRow[]> => {
      const response = await amplifyApiService.get<ICalculationDetailsTableRow[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

/**
 * The function `useGetCalculationById` retrieves a calculation of footprint by its ID using an API call and
 * returns the query result.
 * @param {number} footprintId - The `footprintId` parameter is a number that represents the unique
 * identifier of a footprint.
 * @param {number} calculationId - The `calculationId` parameter is a number that represents the unique
 * identifier of a calculation.
 * @param options - The `options` parameter in the `useGetCalculationById` function is used to provide
 * additional configuration options for the query. These options can include settings such as caching
 * behavior, polling intervals, error handling, and more. By default, the `options` parameter is an
 * empty object `{}` but
 * @returns The `useGetCalculationById` custom hook is being returned, which takes a `footprintId` and `calculationId` string
 * and optional `options` object as parameters and returns a `UseQueryResult<ICalculationResponse>` object. This
 * hook fetches data for a specific calculation of footprint by its ID using the `amplifyApiService` and returns the
 * response as an `ICalculationResponse` object.
 */
export const useGetCalculationById = (
  footprintId: number,
  calculationId: number,
  options: UseQueryOptions<ICalculationResponse, Error> = {},
): UseQueryResult<ICalculationResponse, Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${footprintId}/calculations/${calculationId}`,
    options: {},
  };

  const query = useQuery<ICalculationResponse, Error>({
    queryKey: [BASE_PATH, 'calculation', calculationId],
    queryFn: async (): Promise<ICalculationResponse> => {
      const response = await amplifyApiService.get<ICalculationResponse>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

/**
 * This method is used to publish completed calculation
 * @param {UseMutationOptions<boolean, Error, PublishCalculationProps>} options
 * @returns If the publish was successful it would send a success message or else the appropriate validation messages
 */
export const usePublishCalculation = (
  options: UseMutationOptions<boolean, Error, PublishCalculationProps> = {},
): UseMutationResult<boolean, Error, PublishCalculationProps> => {
  const mutation = useMutation<boolean, Error, PublishCalculationProps>({
    mutationFn: async ({ calculationId, footprintId }: PublishCalculationProps) => {
      const path = `${BASE_PATH}/${footprintId}/calculations/${calculationId}/publish`;

      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: path,
      };

      return amplifyApiService.put<undefined, boolean>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useGetCalculationPdfDownloadUrl = (
  id?: number,
): UseQueryResult<IFootprintPdfDownloadUrl> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${id}/get-calculation-pdf`,
    options: {},
  };

  const query = useQuery<IFootprintPdfDownloadUrl, Error>({
    queryKey: [BASE_PATH, id, 'get-calculation-pdf'],
    queryFn: async (): Promise<IFootprintPdfDownloadUrl> => {
      const response = await amplifyApiService.get<IFootprintPdfDownloadUrl>(amplifyApiConfig);

      return response;
    },
    enabled: false,
  });

  return query;
};

/**
 * This function retrieves footprints by component ID using a specified API configuration and query
 * options.
 * @param {string} componentId - The `componentId` parameter is a string that represents the unique
 * identifier of a component for which you want to retrieve footprints.
 * @param options - The `options` parameter in the `useGetFootprintsByComponentId` function is used to
 * provide additional configuration options for the query. These options can include settings such as
 * caching, polling intervals, error handling, and more. By default, an empty object is provided as the
 * default value for the parameter.`
 * @returns The `useGetFootprintsByComponentId` custom hook is being returned. This hook is used to fetch
 * footprints data by component ID using a specified API configuration and query function.
 */
export const useGetFootprintsByComponentId = (
  componentId: string,
  options: UseQueryOptions<IFootPrintTableRow[], Error> = {},
): UseQueryResult<IFootPrintTableRow[], Error> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams: {
        componentId,
      },
    },
  };

  const query = useQuery<IFootPrintTableRow[], Error>({
    queryKey: [BASE_PATH, 'cId', componentId],
    queryFn: async (): Promise<IFootPrintTableRow[]> => {
      const response = await amplifyApiService.get<IFootPrintTableRow[]>(amplifyApiConfig);

      return response || [];
    },
    ...options,
  });

  return query;
};
